import React, { useEffect, useState } from 'react';
import styles from './css/AdminAreaPage.module.css';
import { AnalystService, AnalystDto } from '../services/analyst.service';
import { ChildService, ChildDto } from '../services/child.service';
import { useSelector } from 'react-redux';
import { selectAuth } from '../redux/auth/auth.selector';
import { safeLogger } from '../utils/logger';

const tableStyle = {
  width: '100%',
  borderCollapse: 'collapse' as const,
  border: '1px solid #8bc34a',
  direction: 'rtl' as const,
};

const thStyle = {
  backgroundColor: '#eafbe6',
  border: '1px solid #8bc34a',
  padding: '12px',
  textAlign: 'right' as const,
};

const tdStyle = {
  border: '1px solid #8bc34a',
  padding: '8px',
  textAlign: 'right' as const,
};

export default function AdminAreaPage() {
  const auth = useSelector(selectAuth);
  const user = auth.user;

  const [analysts, setAnalysts] = useState<AnalystDto[]>([]);
  const [children, setChildren] = useState<ChildDto[]>([]);
  const [form, setForm] = useState({ firstName: '', lastName: '', idNumber: '', email: '' });
  const [editId, setEditId] = useState<number | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [selectedAnalystId, setSelectedAnalystId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [adminEmail, setAdminEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [deleteStep, setDeleteStep] = useState<'idle' | 'email' | 'otp' | 'deleting' | 'add-otp'>('idle');
  const [analystToDelete, setAnalystToDelete] = useState<number | null>(null);

  // Load analysts on mount and after changes
  useEffect(() => {
    const fetchAnalysts = async () => {
      setLoading(true);
      try {
        safeLogger.log('Fetching analysts data');
        const data = await AnalystService.getAll();
        safeLogger.log('Analysts data received successfully');
        if (data && Array.isArray(data)) {
          setAnalysts(data);
          setError(null);
        } else {
          safeLogger.error('Invalid response format for analysts');
          setError('תגובת השרת אינה בפורמט הנכון');
        }
      } catch (e) {
        safeLogger.error('Error fetching analysts');
        setError('שגיאה בטעינת המנתחות');
        setAnalysts([]);
      } finally {
        setLoading(false);
      }
    };
    fetchAnalysts();
  }, []);

  // Load children when selecting analyst
  useEffect(() => {
    if (!selectedAnalystId) return setChildren([]);
    const fetchChildren = async () => {
      setLoading(true);
      try {
        const all = await ChildService.getAll();
        // נוודא שהמיפוי נכון: analystId === code
        setChildren(all.filter(child => Number(child.analystId) === Number(selectedAnalystId)));
      } catch (e) {
        setError('שגיאה בטעינת הילדים');
      } finally {
        setLoading(false);
      }
    };
    fetchChildren();
  }, [selectedAnalystId]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // הוספת מנתחת - שלב ראשון: שליחת קוד אימות
  const handleAdd = async () => {
    if (!form.firstName || !form.lastName || !form.idNumber || !form.email) return;
    setLoading(true);
    try {
      // שלח קוד אימות למייל של המנהלת
      await fetch('/api/login/send-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: user?.email, id: user?.id })
      });
      setDeleteStep('add-otp'); // שלב חדש לאימות הוספה
    } catch (e) {
      setError('שגיאה בשליחת קוד אימות');
      setDeleteStep('idle');
    } finally {
      setLoading(false);
    }
  };

  // אימות קוד OTP והוספה בפועל
  const verifyOtpAndAdd = async () => {
    setLoading(true);
    setError(null);
    safeLogger.log('Verifying OTP for analyst addition');
    try {
      const res = await fetch('/api/login/verify-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: user?.email, code: otp, id: user?.id })
      });
      if (!res.ok) throw new Error('קוד אימות שגוי');
      // אם הצליח, הוסף את המנתחת
      const newAnalyst = await AnalystService.add(form);
      setAnalysts([...analysts, newAnalyst]);
      setShowForm(false);
      setForm({ firstName: '', lastName: '', idNumber: '', email: '' });
      setDeleteStep('idle');
      setOtp('');
    } catch (e) {
      setError('קוד אימות שגוי או שגיאה בהוספה');
      setDeleteStep('add-otp');
    } finally {
      setLoading(false);
    }
  };

  // מחיקת מנתחת - שלב ראשון: שליחת קוד אוטומטית
  const handleDelete = async (id: number) => {
    setAnalystToDelete(id);
    setError(null);
    setLoading(true);
    try {
      await fetch('/api/login/send-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: user?.email, id: user?.id })
      });
      setDeleteStep('otp');
    } catch (e) {
      setError('שגיאה בשליחת קוד אימות');
      setDeleteStep('idle');
    } finally {
      setLoading(false);
    }
  };

  // אימות קוד OTP ומחיקה בפועל
  const verifyOtpAndDelete = async () => {
    setLoading(true);
    setError(null);
    safeLogger.log('Verifying OTP for analyst deletion');
    try {
      const res = await fetch('/api/login/verify-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: user?.email, code: otp, id: user?.id })
      });
      if (!res.ok) throw new Error('קוד אימות שגוי');
      setDeleteStep('deleting');
      await AnalystService.delete(analystToDelete!);
      setAnalysts(analysts.filter(a => a.code !== analystToDelete));
      if (selectedAnalystId === analystToDelete) setSelectedAnalystId(null);
      setDeleteStep('idle');
      setAnalystToDelete(null);
      setOtp('');
    } catch (e) {
      setError('קוד אימות שגוי או שגיאה במחיקה');
      setDeleteStep('otp');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (id: number) => {
    const analyst = analysts.find(a => a.code === id);
    if (analyst) {
      setForm({
        firstName: analyst.firstName,
        lastName: analyst.lastName,
        idNumber: analyst.idNumber,
        email: analyst.email,
      });
      setEditId(id);
      setShowForm(true);
    }
  };

  const handleUpdate = async () => {
    if (editId == null) return;
    setLoading(true);
    try {
      await AnalystService.update(editId, { code: editId, ...form });
      setAnalysts(analysts.map(a => a.code === editId ? { code: editId, ...form } : a));
      setEditId(null);
      setShowForm(false);
      setForm({ firstName: '', lastName: '', idNumber: '', email: '' });
    } catch (e) {
      setError('שגיאה בעדכון מנתחת');
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = (id: number) => {
    setSelectedAnalystId(id === selectedAnalystId ? null : id);
  };

  return (
    <div style={{ direction: 'rtl', background: '#fffbe6', minHeight: '100vh', padding: 0, paddingTop: '180px' }}>
      <h1 style={{ textAlign: 'center', fontSize: '6rem', margin: 0, padding: '2rem 0', background: '#ffe599', direction: 'rtl', position: 'fixed', top: '0', left: '0', right: '0', zIndex: '98' }}>
        ניהול אתר
      </h1>
      {error && <div style={{ color: 'red', textAlign: 'center', margin: '10px 0' }}>{error}</div>}
      {loading && <div style={{ textAlign: 'center', margin: '10px 0' }}>טוען...</div>}
      
      <div style={{ maxWidth: '1200px', margin: '2rem auto' }}>
        <div style={{ marginBottom: '20px' }}>
          <button 
            style={{ 
              background: '#b35409', 
              color: 'white', 
              fontSize: '1.3rem', 
              padding: '0.5rem 2rem', 
              border: 'none', 
              borderRadius: 3, 
              cursor: 'pointer',
              float: 'left'
            }} 
            onClick={() => { 
              setShowForm(true); 
              setEditId(null); 
              setForm({ firstName: '', lastName: '', idNumber: '', email: '' }); 
            }}
          >
            הוספת מנתחת
          </button>
        </div>

        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>שם פרטי</th>
              <th style={thStyle}>שם משפחה</th>
              <th style={thStyle}>ת.ז.</th>
              <th style={thStyle}>אימייל</th>
              <th style={thStyle}>פעולות</th>
            </tr>
          </thead>
          <tbody>
            {analysts.map(a => (
              <tr 
                key={a.code} 
                style={{ 
                  cursor: 'pointer',
                  backgroundColor: selectedAnalystId === a.code ? '#b6e1a7' : '#ffffff',
                }}
                onClick={() => handleRowClick(a.code)}
              >
                <td style={tdStyle}>{a.firstName || '--'}</td>
                <td style={tdStyle}>{a.lastName || '--'}</td>
                <td style={tdStyle}>{a.idNumber || '--'}</td>
                <td style={tdStyle}>{a.email || '--'}</td>
                <td style={{ ...tdStyle, width: '120px', textAlign: 'center' }}>
                  <button 
                    onClick={(e) => { e.stopPropagation(); handleEdit(a.code); }} 
                    style={{ 
                      marginLeft: '10px',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                    }}
                    title="ערוך"
                  >
                    ✏️
                  </button>
                  <button 
                    onClick={(e) => { e.stopPropagation(); handleDelete(a.code); }} 
                    style={{ 
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                    }}
                    title="מחק"
                  >
                    🗑️
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* טבלת ילדים */}
      {selectedAnalystId && (
        <div style={{ maxWidth: '1200px', margin: '2rem auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', direction: 'rtl', fontSize: '1.1rem', boxShadow: '0 2px 8px #b6b6b6' }}>
            <thead>
              <tr style={{ background: '#4177c2' }}>
                <th style={{ color: 'white', padding: '14px', fontWeight: 700, border: '1px solid #4177c2' }}>שם ילד</th>
                <th style={{ color: 'white', padding: '14px', fontWeight: 700, border: '1px solid #4177c2' }}>תעודת זהות</th>
                <th style={{ color: 'white', padding: '14px', fontWeight: 700, border: '1px solid #4177c2' }}>EMAIL</th>
                <th style={{ color: 'white', padding: '14px', fontWeight: 700, border: '1px solid #4177c2' }}>גיל</th>
                <th style={{ color: 'white', padding: '14px', fontWeight: 700, border: '1px solid #4177c2' }}>מוסד לימודים</th>
              </tr>
            </thead>
            <tbody>
              {children.map((child, idx) => (
                <tr key={child.id} style={{ background: idx % 2 === 0 ? '#e6edfa' : '#f4f7fb' }}>
                  <td style={{ padding: '10px', border: '1px solid #b6b6b6', textAlign: 'center' }}>{child.name || '--'}</td>
                  <td style={{ padding: '10px', border: '1px solid #b6b6b6', textAlign: 'center' }}>{child.idNumber || '--'}</td>
                  <td style={{ padding: '10px', border: '1px solid #b6b6b6', textAlign: 'center', direction: 'ltr' }}>{child.email || '--'}</td>
                  <td style={{ padding: '10px', border: '1px solid #b6b6b6', textAlign: 'center' }}>{child.age || '--'}</td>
                  <td style={{ padding: '10px', border: '1px solid #b6b6b6', textAlign: 'center' }}>{child.school || '--'}</td>
                </tr>
              ))}
              {children.length === 0 && (
                <tr>
                  <td colSpan={5} style={{ textAlign: 'center', padding: '18px', color: '#888' }}>
                    אין ילדים למנתחת זו
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* טופס הוספה/עריכה */}
      {showForm && (
        <div style={{ 
          background: '#fde9d9', 
          border: '1px solid #b35409', 
          maxWidth: '650px', 
          margin: '2rem auto', 
          padding: '2rem', 
          borderRadius: 6,
          direction: 'rtl'
        }}>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '5px' }}>שם פרטי:</label>
            <input
              style={{ 
                width: '100%', 
                padding: '8px', 
                marginBottom: '15px',
                direction: 'rtl'
              }}
              name="firstName"
              value={form.firstName}
              onChange={handleChange}
            />
            <label style={{ display: 'block', marginBottom: '5px' }}>שם משפחה:</label>
            <input
              style={{ 
                width: '100%', 
                padding: '8px', 
                marginBottom: '15px',
                direction: 'rtl'
              }}
              name="lastName"
              value={form.lastName}
              onChange={handleChange}
            />
            <label style={{ display: 'block', marginBottom: '5px' }}>תעודת זהות:</label>
            <input
              style={{ 
                width: '100%', 
                padding: '8px', 
                marginBottom: '15px',
                direction: 'rtl'
              }}
              name="idNumber"
              value={form.idNumber}
              onChange={handleChange}
            />
            <label style={{ display: 'block', marginBottom: '5px' }}>אימייל:</label>
            <input
              style={{ 
                width: '100%', 
                padding: '8px', 
                marginBottom: '15px',
                direction: 'rtl'
              }}
              name="email"
              value={form.email}
              onChange={handleChange}
            />
          </div>
          <div style={{ textAlign: 'left' }}>
            <button
              style={{ 
                background: '#b35409', 
                color: 'white', 
                fontSize: '1.3rem', 
                padding: '0.5rem 3rem', 
                border: 'none', 
                borderRadius: 3, 
                cursor: 'pointer',
                marginLeft: '10px'
              }}
              onClick={editId ? handleUpdate : handleAdd}
            >
              {editId ? 'עדכן' : 'הוסף'}
            </button>
            <button
              style={{ 
                background: '#888', 
                color: 'white', 
                fontSize: '1.3rem', 
                padding: '0.5rem 2rem', 
                border: 'none', 
                borderRadius: 3, 
                cursor: 'pointer'
              }}
              onClick={() => { 
                setShowForm(false); 
                setEditId(null); 
                setForm({ firstName: '', lastName: '', idNumber: '', email: '' }); 
              }}
            >
              ביטול
            </button>
          </div>
        </div>
      )}

      {/* דיאלוג מחיקה מאובטחת */}
      {deleteStep === 'otp' && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.3)', zIndex: 1000 }}>
          <div style={{ background: '#fff', maxWidth: 400, margin: '10% auto', padding: 30, borderRadius: 8, textAlign: 'center' }}>
            <h3>נשלח קוד אימות למייל שלך, אנא אמת</h3>
            <input
              type="text"
              placeholder="קוד אימות"
              value={otp}
              onChange={e => setOtp(e.target.value)}
              style={{ width: '90%', padding: 8, marginBottom: 16 }}
            />
            <br />
            <button onClick={verifyOtpAndDelete} style={{ background: '#b35409', color: 'white', padding: '8px 24px', border: 'none', borderRadius: 3, cursor: 'pointer' }}>מחק</button>
            <button onClick={() => { setDeleteStep('idle'); setAnalystToDelete(null); setOtp(''); }} style={{ marginRight: 10 }}>ביטול</button>
          </div>
        </div>
      )}

      {/* דיאלוג אימות הוספה */}
      {deleteStep === 'add-otp' && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.3)', zIndex: 1000 }}>
          <div style={{ background: '#fff', maxWidth: 400, margin: '10% auto', padding: 30, borderRadius: 8, textAlign: 'center' }}>
            <h3>נשלח קוד אימות למייל שלך, אנא אמת כדי להוסיף מנתחת</h3>
            <input
              type="text"
              placeholder="קוד אימות"
              value={otp}
              onChange={e => setOtp(e.target.value)}
              style={{ width: '90%', padding: 8, marginBottom: 16 }}
            />
            <br />
            <button onClick={verifyOtpAndAdd} style={{ background: '#b35409', color: 'white', padding: '8px 24px', border: 'none', borderRadius: 3, cursor: 'pointer' }}>הוסף</button>
            <button onClick={() => { setDeleteStep('idle'); setOtp(''); setShowForm(false); }} style={{ marginRight: 10 }}>ביטול</button>
          </div>
        </div>
      )}
    </div>
  );
}
